import { supabase } from '@/lib/supabase';

/** Événement publié par un commerce (table `events`). */
export interface PublicEvent {
  id: number;
  title: string | null;
  description: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  status: number | null;
  id_user: string | null;
  code: string | null;
  id_haut: string | null;
  id_milieu: string | null;
  id_bas: string | null;
  /** Nombre total de places (null = illimité). */
  capacity?: number | null;
  /** Inscriptions ouvertes (fermées automatiquement quand c'est complet). */
  registration_open?: boolean | null;
  /** Prix d'entrée (null ou 0 = gratuit). */
  price?: number | null;
  /** Devise du prix. */
  currency?: string | null;
  /** Lieu de l'événement (facultatif). */
  location?: string | null;
}

const EVENT_COLUMNS = 'id, title, description, start_datetime, end_datetime, status, id_user, code, id_haut, id_milieu, id_bas, capacity, registration_open, price, currency, location';

/** Vrai si l'événement est payant. */
export function isEventPaid(ev: PublicEvent): boolean {
  return Number(ev.price || 0) > 0;
}

/** Montant total pour un nombre de places donné. */
export function eventTotal(ev: PublicEvent, seats: number): number {
  const unit = Number(ev.price || 0);
  if (!(unit > 0)) return 0;
  return Math.round(unit * Math.max(1, seats) * 100) / 100;
}

export function isHttpUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^https?:\/\//i.test(value.trim());
}

/** Image de l'événement : première colonne contenant une vraie URL. */
export function eventImage(ev: PublicEvent): string {
  const candidates = [ev.id_haut, ev.id_milieu, ev.id_bas, ev.code];
  const found = candidates.find((c) => isHttpUrl(c));
  return found ? String(found).trim() : '';
}

export function eventDateLabel(value: string | null): string {
  if (!value) return 'Date à venir';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Date à venir';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export function eventShortDate(value: string | null): { day: string; month: string; full: string } {
  if (!value) return { day: '--', month: '---', full: 'Date à venir' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { day: '--', month: '---', full: 'Date à venir' };
  return {
    day: d.toLocaleDateString('fr-FR', { day: '2-digit' }),
    month: d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', ''),
    full: eventDateLabel(value),
  };
}

export function eventTimeLabel(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  if (d.getHours() === 0 && d.getMinutes() === 0) return '';
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export function eventEndLabel(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function isEventPast(ev: PublicEvent): boolean {
  const ref = ev.end_datetime || ev.start_datetime;
  if (!ref) return false;
  const d = new Date(ref);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

/** Charge les événements d'un commerce (uniquement ceux publiés). */
export async function fetchMerchantEvents(merchantId: number): Promise<PublicEvent[]> {
  const { data, error } = await supabase
    .from('events')
    .select(EVENT_COLUMNS)
    .eq('id_user', String(merchantId))
    .order('start_datetime', { ascending: true });
  if (error) throw error;
  const rows = (data || []) as PublicEvent[];
  // status : 1 = publié (les autres valeurs restent des brouillons).
  return rows.filter((r) => r.status == null || Number(r.status) === 1);
}

export async function fetchMerchantEvent(merchantId: number, eventId: number): Promise<PublicEvent | null> {
  const { data, error } = await supabase
    .from('events')
    .select(EVENT_COLUMNS)
    .eq('id', eventId)
    .eq('id_user', String(merchantId))
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const ev = data as PublicEvent;
  if (ev.status != null && Number(ev.status) !== 1) return null;
  return ev;
}